import { HTMLAttributes, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, LinkProps, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { signIn, fetchMFAPreference } from "aws-amplify/auth";
import { match } from "ts-pattern";
import { cn } from "@/lib/utils";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/password-input";
import { StatusButton } from "@/components/ui/loading-button";

type AuthChallengeType =
  | "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
  | "CONFIRM_SIGN_IN_WITH_TOTP_CODE"
  | "CONTINUE_SIGN_IN_WITH_TOTP_SETUP"
  | "DONE";

const formSchema = z.object({
  email: z
    .string()
    .min(1, "Please enter your email")
    .email("Invalid email address"),
  password: z
    .string()
    .min(1, "Please enter your password")
    .min(7, "Password must be at least 7 characters long"),
});

type FormValues = z.infer<typeof formSchema>;

type UserAuthFormProps = HTMLAttributes<HTMLDivElement> & {
  isSuperadmin?: boolean;
};

export function UserAuthForm({
  className,
  isSuperadmin = false,
  ...props
}: UserAuthFormProps) {
  const navigate = useNavigate();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "", password: "" },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAuthChallenge = useCallback(
    (challengeType: AuthChallengeType) => {
      const baseRoutes: Record<
        AuthChallengeType,
        LinkProps["to"] | (() => void)
      > = {
        CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED: "/new-password",
        CONFIRM_SIGN_IN_WITH_TOTP_CODE: "/otp",
        CONTINUE_SIGN_IN_WITH_TOTP_SETUP: "/totp-setup",
        DONE: () => void navigate({ to: "/dashboard" }),
      };
      const superadminRoutes: Record<AuthChallengeType, LinkProps["to"]> = {
        CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED: "/root-user/new-password",
        CONFIRM_SIGN_IN_WITH_TOTP_CODE: "/root-user/otp",
        CONTINUE_SIGN_IN_WITH_TOTP_SETUP: "/root-user/totp-setup",
        DONE: "/root-user/dashboard",
      };
      const routes = isSuperadmin ? superadminRoutes : baseRoutes;
      const route = routes[challengeType];

      match(challengeType)
        .with("CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED", () => {
          toast.success("Login successful!");
          toast.info("Please set a new password for your account.");
        })
        .with("CONFIRM_SIGN_IN_WITH_TOTP_CODE", () => {
          toast.success("Login successful!");
          toast.info("Please enter your OTP code to continue.");
        })
        .with("CONTINUE_SIGN_IN_WITH_TOTP_SETUP", () => {
          toast.success("Login successful!");
          toast.info("Please set up two-factor authentication to continue.");
        })
        .with("DONE", () => {
          toast.success("Login successful! Redirecting to dashboard...");
        })
        .exhaustive();

      typeof route === "function" ? route() : void navigate({ to: route });
    },
    [isSuperadmin, navigate],
  );

  const onSubmit = async (data: FormValues) => {
    try {
      setIsSubmitting(true);
      const { nextStep } = await signIn({
        username: data.email,
        password: data.password,
      });
      handleAuthChallenge(nextStep.signInStep as AuthChallengeType);
    } catch (error) {
      match(error)
        .with({ name: "UserAlreadyAuthenticatedException" }, () => {
          toast.warning("You are already logged in");
          void navigate({
            to: isSuperadmin ? "/root-user/dashboard" : "/dashboard",
          });
        })
        .otherwise((err) => {
          toast.error("Incorrect username or password");
          console.error(err);
        });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="name@example.com"
                      autoComplete="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <div className="flex items-center justify-between">
                    <FormLabel>Password</FormLabel>
                    {!isSuperadmin && (
                      <Link
                        to="/forgot-password"
                        className="text-muted-foreground text-sm font-medium hover:opacity-75"
                      >
                        Forgot password?
                      </Link>
                    )}
                  </div>
                  <FormControl>
                    <PasswordInput
                      placeholder="********"
                      autoComplete="current-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <StatusButton
              type="submit"
              className="mt-2"
              isLoading={isSubmitting}
            >
              {isSuperadmin ? "Superadmin Login" : "Login"}
            </StatusButton>

            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
